import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Minus, Plus, Trash2, Loader2, ShoppingBag, Package } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AlertModal from '../components/AlertModal';
import { orderAPI, productAPI } from '../utils/api';

function CartPage() {
  const [cart, setCart] = useState(null);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'info', message: '' });
  const navigate = useNavigate();

  const showAlert = (type, message) => {
    setAlertModal({ isOpen: true, type, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', message: '' });
  };

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    try {
      const data = await orderAPI.getCart();
      setCart(data);

      // 장바구니에 있는 상품들의 상세 정보 가져오기
      if (data.items && data.items.length > 0) {
        const productMap = {};
        for (const item of data.items) {
          try {
            const product = await productAPI.getDetail(item.product_uuid);
            productMap[item.product_uuid] = product;
          } catch (err) {
            console.error(`상품 정보 로드 실패: ${item.product_uuid}`, err);
          }
        }
        setProducts(productMap);
      }
    } catch (err) {
      showAlert('error', '장바구니를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productUuid, newQuantity, optionName) => {
    if (newQuantity < 1) {
      if (!window.confirm('상품을 장바구니에서 제거하시겠습니까?')) {
        return;
      }
      await removeItem(productUuid, optionName);
      return;
    }

    try {
      const result = await orderAPI.updateCartItem(productUuid, newQuantity, optionName);
      setCart(result.cart);
    } catch (err) {
      showAlert('error', '수량 변경에 실패했습니다.');
      console.error(err);
    }
  };

  const removeItem = async (productUuid, optionName) => {
    try {
      const result = await orderAPI.removeCartItem(productUuid, optionName);
      setCart(result.cart);
    } catch (err) {
      showAlert('error', '상품 삭제에 실패했습니다.');
      console.error(err);
    }
  };

  const goToCheckout = () => {
    if (!cart || !cart.items || cart.items.length === 0) {
      showAlert('error', '장바구니가 비어있습니다.');
      return;
    }
    navigate('/checkout', { state: { cart, products } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">장바구니</h1>
          <div className="max-w-4xl mx-auto">
            {/* 스켈레톤 로딩 UI */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <div className="space-y-6">
                {[1, 2].map((i) => (
                  <div key={i} className="flex items-start gap-4 pb-6 border-b border-gray-100 last:border-b-0 last:pb-0 animate-pulse">
                    <div className="w-24 h-24 bg-gray-200 rounded-lg flex-shrink-0" />
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-3" />
                      <div className="h-10 bg-gray-200 rounded w-32" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* 주문 요약 스켈레톤 */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-24 mb-4" />
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20" />
                  <div className="h-4 bg-gray-200 rounded w-24" />
                </div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-16" />
                  <div className="h-4 bg-gray-200 rounded w-20" />
                </div>
                <div className="border-t border-gray-200 pt-4 flex justify-between">
                  <div className="h-5 bg-gray-200 rounded w-24" />
                  <div className="h-5 bg-gray-200 rounded w-28" />
                </div>
              </div>
              <div className="h-14 bg-gray-200 rounded-lg" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isEmpty = !cart || !cart.items || cart.items.length === 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">장바구니</h1>

        {isEmpty ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="text-gray-400 mb-4">
              <ShoppingBag className="w-16 h-16 mx-auto" />
            </div>
            <p className="text-lg text-gray-600 mb-2">장바구니가 비어있습니다.</p>
            <p className="text-sm text-gray-400 mb-6">원하는 상품을 담아보세요.</p>
            <button
              onClick={() => navigate('/products')}
              className="bg-teal-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors"
            >
              쇼핑 계속하기
            </button>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <div className="space-y-6">
                {cart.items.map((item, index) => {
                  const product = products[item.product_uuid];
                  const images = product?.image_urls || (product?.image_url ? [product.image_url] : []);
                  // 옵션 가격 계산
                  const optionName = item.option_name || null;
                  const optionObj = optionName && product?.options
                    ? product.options.find(o => o.name === optionName)
                    : null;
                  const itemPrice = optionObj ? optionObj.price : (product?.price || 0);
                  return (
                    <div key={index} className="flex items-start gap-4 pb-6 border-b border-gray-100 last:border-b-0 last:pb-0">
                      {/* 상품 이미지 */}
                      <div className="relative flex-shrink-0">
                        {images.length > 0 ? (
                          <img
                            src={images[0]}
                            alt={product?.name || '상품'}
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs">
                            이미지 없음
                          </div>
                        )}
                        {(() => {
                          const hasFast = optionObj
                            ? (optionObj.fassto_stock_qty ?? 0) > 0
                            : product?.has_fast_shipping;
                          return hasFast && (
                            <span className="absolute -top-1 -left-1 bg-teal-500 text-white p-1 rounded-full shadow-md">
                              <Zap className="w-3 h-3" />
                            </span>
                          );
                        })()}
                      </div>

                      {/* 상품 정보 */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {product?.name || '상품 정보 로딩 중...'}
                        </h3>

                        {optionName && (
                          <div className="inline-flex items-center text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded mb-2">
                            옵션: {optionName}
                          </div>
                        )}

                        {(() => {
                          const stockQty = optionObj
                            ? (optionObj.fassto_stock_qty ?? null)
                            : (product?.has_fast_shipping ? product.fassto_stock_qty : null);
                          return stockQty > 0 && (
                            <div className="flex items-center gap-1 text-xs text-teal-600 mb-2">
                              <Package className="w-3.5 h-3.5" />
                              <span>빠른 배송 · 재고 {stockQty}개</span>
                            </div>
                          );
                        })()}

                        {itemPrice > 0 && (
                          <p className="text-gray-500 text-sm mb-3">
                            {itemPrice.toLocaleString()}원
                          </p>
                        )}

                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                            <button
                              onClick={() => updateQuantity(item.product_uuid, item.quantity - 1, optionName)}
                              className="p-2 text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (!isNaN(value) && value >= 1) {
                                  updateQuantity(item.product_uuid, value, optionName);
                                }
                              }}
                              onBlur={(e) => {
                                const value = parseInt(e.target.value);
                                if (isNaN(value) || value < 1) {
                                  updateQuantity(item.product_uuid, 1, optionName);
                                }
                              }}
                              className="w-14 text-center font-medium bg-white border-x border-gray-300 py-1 focus:outline-none"
                            />
                            <button
                              onClick={() => updateQuantity(item.product_uuid, item.quantity + 1, optionName)}
                              className="p-2 text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          {itemPrice > 0 && (
                            <p className="font-bold text-lg text-teal-600">
                              {(itemPrice * item.quantity).toLocaleString()}원
                            </p>
                          )}
                        </div>
                      </div>

                      {/* 삭제 버튼 */}
                      <button
                        onClick={() => removeItem(item.product_uuid, optionName)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        title="삭제"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 주문 요약 */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">주문 요약</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>상품 금액</span>
                  <span className="font-medium text-gray-900">{cart.subtotal?.toLocaleString() || 0}원</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>배송비</span>
                  <span className="font-medium text-gray-900">{cart.shipping_fee?.toLocaleString() || 0}원</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-4">
                  <span className="text-gray-900">총 결제금액</span>
                  <span className="text-teal-600">{cart.total_cost?.toLocaleString() || 0}원</span>
                </div>
              </div>

              <button
                onClick={goToCheckout}
                className="w-full bg-teal-500 text-white py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors"
              >
                주문하기
              </button>
            </div>
          </div>
        )}
      </main>

      <Footer />

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        message={alertModal.message}
      />
    </div>
  );
}

export default CartPage;
